import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogOut, Newspaper, Home, Megaphone, FileEdit, Inbox, Settings as SettingsIcon,
  Save, Upload, Trash2, Plus, GripVertical, Eye, EyeOff, RefreshCw,
  Facebook, Twitter, Instagram, Youtube, Linkedin,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { settingsAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const AdminSidebar = ({ onLogout, user }) => (
  <aside className="fixed left-0 top-0 h-full w-64 bg-[#0F172A] text-white p-4 z-50">
    <div className="mb-8">
      <h1 className="text-lg font-bold tracking-tight">HABER TÜRKİYE'DEN</h1>
      <p className="text-gray-400 text-xs mt-1">Yönetim Paneli</p>
    </div>
    <nav className="space-y-1">
      <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
        <Newspaper className="w-5 h-5" /><span>Haberler</span>
      </Link>
      <Link to="/admin/talepler" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
        <Inbox className="w-5 h-5" /><span>Gelen Talepler</span>
      </Link>
      <Link to="/admin/reklamlar" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
        <Megaphone className="w-5 h-5" /><span>Reklam Yönetimi</span>
      </Link>
      <Link to="/admin/ayarlar" className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/10 text-white">
        <SettingsIcon className="w-5 h-5" /><span className="font-medium">Site Ayarları</span>
      </Link>
      <div className="pt-4 pb-1">
        <p className="px-3 text-xs uppercase font-bold text-gray-500 tracking-wider">Sayfa Yönetimi</p>
      </div>
      <Link to="/admin/sayfa/hakkimizda" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
        <FileEdit className="w-5 h-5" /><span>Hakkımızda</span>
      </Link>
      <Link to="/admin/sayfa/reklam-isbirligi" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
        <FileEdit className="w-5 h-5" /><span>Reklam & İş Birliği</span>
      </Link>
      <div className="pt-4">
        <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
          <Home className="w-5 h-5" /><span>Siteyi Görüntüle</span>
        </Link>
      </div>
    </nav>
    <div className="absolute bottom-4 left-4 right-4">
      <div className="border-t border-gray-700 pt-4 mb-4">
        <p className="text-gray-400 text-sm">{user?.username}</p>
      </div>
      <Button variant="ghost" className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5" onClick={onLogout}>
        <LogOut className="w-4 h-4 mr-2" />Çıkış Yap
      </Button>
    </div>
  </aside>
);

const Section = ({ title, description, children, testId }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-6" data-testid={testId}>
    <div className="mb-5">
      <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      {description && <p className="text-sm text-gray-500 mt-1">{description}</p>}
    </div>
    {children}
  </div>
);

const BrandUploader = ({ kind, label, preview, onUpdate, previewClass }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Geçerli bir resim dosyası seçin');
    setBusy(true);
    try {
      const res = await settingsAPI.uploadAsset(kind, file);
      onUpdate(res.data);
      toast.success(`${label} güncellendi`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Yüklenemedi');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      const res = await settingsAPI.deleteAsset(kind);
      onUpdate(res.data);
      toast.success(`${label} kaldırıldı`);
    } catch {
      toast.error('Silinemedi');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">{label}</label>
      <div className="flex items-center gap-4">
        <div className={`bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg flex items-center justify-center overflow-hidden ${previewClass}`}>
          {preview ? (
            <img src={preview} alt={label} className="w-full h-full object-contain" />
          ) : (
            <span className="text-xs text-gray-400">Yüklü değil</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid={`brand-file-${kind}`} />
          <Button type="button" size="sm" onClick={() => fileRef.current?.click()} disabled={busy} className="bg-[#0284C7] hover:bg-[#0369A1]" data-testid={`brand-upload-${kind}`}>
            {busy ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> İşleniyor…</> : <><Upload className="w-4 h-4 mr-2" /> {preview ? 'Değiştir' : 'Yükle'}</>}
          </Button>
          {preview && (
            <Button type="button" size="sm" variant="outline" onClick={handleDelete} disabled={busy} className="text-red-600 border-red-200 hover:bg-red-50">
              <Trash2 className="w-4 h-4 mr-1" /> Kaldır
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const AdminSettings = () => {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const { settings: liveSettings, loaded, refresh } = useSettings();
  const navigate = useNavigate();

  // Local editing state (initialised from context)
  const [general, setGeneral] = useState({ site_title: '', site_tagline: '', meta_description: '', head_code: '' });
  const [categories, setCategories] = useState([]);
  const [social, setSocial] = useState({ facebook: '', twitter: '', instagram: '', youtube: '', linkedin: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/admin/giris');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (loaded) {
      setGeneral({
        site_title: liveSettings.site_title || '',
        site_tagline: liveSettings.site_tagline || '',
        meta_description: liveSettings.meta_description || '',
        head_code: liveSettings.head_code || '',
      });
      setCategories(liveSettings.categories || []);
      setSocial(liveSettings.social || { facebook: '', twitter: '', instagram: '', youtube: '', linkedin: '' });
    }
  }, [loaded, liveSettings]);

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await settingsAPI.update({
        site_title: general.site_title,
        site_tagline: general.site_tagline,
        meta_description: general.meta_description,
        head_code: general.head_code,
        categories,
        social,
      });
      await refresh();
      toast.success('Tüm ayarlar kaydedildi');
    } catch (e) {
      toast.error('Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const updateCategory = (idx, patch) => {
    setCategories((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const removeCategory = (idx) => {
    setCategories((prev) => prev.filter((_, i) => i !== idx));
  };

  const addCategory = () => {
    setCategories((prev) => [...prev, { slug: '', label: '', visible: true, _new: true }]);
  };

  if (authLoading || !loaded) {
    return <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center"><div className="text-gray-500">Yükleniyor...</div></div>;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-settings">
      <AdminSidebar user={user} onLogout={() => { logout(); navigate('/admin/giris'); }} />
      <main className="ml-64 p-8 max-w-5xl">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Site Ayarları</h2>
            <p className="text-gray-500 text-sm mt-1">Site başlığı, navigasyon, marka ve sosyal medya bağlantılarını tek yerden yönetin.</p>
          </div>
          <Button onClick={handleSaveAll} disabled={saving} className="bg-[#0284C7] hover:bg-[#0369A1]" data-testid="settings-save-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Kaydediliyor…' : 'Tümünü Kaydet'}
          </Button>
        </div>

        <div className="grid gap-5">
          {/* General */}
          <Section
            testId="section-general"
            title="Genel Ayarlar"
            description="Header ve tarayıcı sekmesinde görünen site adı, SEO meta açıklaması."
          >
            <div className="grid gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Site Başlığı</label>
                <Input
                  value={general.site_title}
                  onChange={(e) => setGeneral({ ...general, site_title: e.target.value })}
                  placeholder="Haber Türkiye'den"
                  data-testid="settings-site-title"
                />
                <p className="text-xs text-gray-400 mt-1">Header, tarayıcı sekmesi ve tüm SEO tag'lerinde kullanılır.</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Slogan / Alt Başlık</label>
                <Input
                  value={general.site_tagline}
                  onChange={(e) => setGeneral({ ...general, site_tagline: e.target.value })}
                  placeholder="Türkiye'den"
                  data-testid="settings-site-tagline"
                />
                <p className="text-xs text-gray-400 mt-1">Logo altında kırmızı küçük metin olarak görünür (logo yüklü değilse).</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">SEO Meta Açıklaması</label>
                <Textarea
                  value={general.meta_description}
                  onChange={(e) => setGeneral({ ...general, meta_description: e.target.value })}
                  rows={3}
                  placeholder="Site hakkında kısa açıklama (150–160 karakter ideal)"
                  data-testid="settings-meta-desc"
                />
                <p className="text-xs text-gray-400 mt-1">{general.meta_description.length} / 160 karakter önerilen</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                  Google Search Console Doğrulama Kodu
                </label>
                <Textarea
                  value={general.head_code}
                  onChange={(e) => setGeneral({ ...general, head_code: e.target.value })}
                  rows={5}
                  className="font-mono text-xs leading-relaxed"
                  placeholder={'<meta name="google-site-verification" content="..." />\n\n<!-- veya Google Analytics, Tag Manager, vb. -->'}
                  data-testid="settings-head-code"
                  spellCheck={false}
                />
                <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                  Buraya yapıştırdığınız HTML (meta etiket, Google Analytics, Tag Manager vb.) site genelinde otomatik olarak <code className="bg-gray-100 px-1 rounded text-gray-600">&lt;head&gt;</code> içine eklenir. Yalnızca güvendiğiniz kodları yapıştırın.
                </p>
              </div>
            </div>
          </Section>

          {/* Branding */}
          <Section
            testId="section-branding"
            title="Marka Görselleri"
            description="Header/Footer logosu ve tarayıcı sekmesinde görünen favicon."
          >
            <div className="grid gap-6 md:grid-cols-2">
              <BrandUploader
                kind="logo"
                label="Site Logosu (Header & Footer)"
                preview={liveSettings.logo_data}
                onUpdate={() => refresh()}
                previewClass="h-16 w-40 bg-white"
              />
              <BrandUploader
                kind="favicon"
                label="Favicon (Tarayıcı Sekmesi)"
                preview={liveSettings.favicon_data}
                onUpdate={() => refresh()}
                previewClass="h-16 w-16"
              />
            </div>
            <p className="text-xs text-gray-400 mt-4">
              Logo PNG olarak saklanır, favicon otomatik 64×64 px'e ölçeklenir. Şeffaf arka plan desteklenir.
            </p>
          </Section>

          {/* Categories */}
          <Section
            testId="section-categories"
            title="Navigasyon & Kategoriler"
            description="Header ve Footer'da görünen kategorileri yönetin. Eklenen kategori slug'ı, haber kaydederken seçilebilir olur."
          >
            <div className="space-y-2">
              {categories.map((c, idx) => (
                <div key={idx} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid={`category-row-${idx}`}>
                  <GripVertical className="w-4 h-4 text-gray-300 flex-shrink-0" />
                  <Input
                    value={c.slug}
                    onChange={(e) => updateCategory(idx, { slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="slug"
                    className="w-40 font-mono text-sm"
                    disabled={!c._new}
                    title={c._new ? '' : 'Mevcut kategori slug\'ı değiştirilemez — haberler buna bağlı'}
                    data-testid={`category-slug-${idx}`}
                  />
                  <Input
                    value={c.label}
                    onChange={(e) => updateCategory(idx, { label: e.target.value })}
                    placeholder="Görünen ad"
                    className="flex-1"
                    data-testid={`category-label-${idx}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => updateCategory(idx, { visible: !c.visible })}
                    className={c.visible ? 'text-green-600 border-green-200' : 'text-gray-400'}
                    title={c.visible ? 'Gizle' : 'Göster'}
                    data-testid={`category-toggle-${idx}`}
                  >
                    {c.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => removeCategory(idx)}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                    data-testid={`category-remove-${idx}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button type="button" variant="outline" onClick={addCategory} className="w-full border-dashed" data-testid="category-add">
                <Plus className="w-4 h-4 mr-2" /> Yeni Kategori Ekle
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-3">
              <strong>Not:</strong> Mevcut bir kategorinin <em>slug</em>'ı değiştirilemez çünkü haberler buna bağlıdır. Sadece <em>görünen ad</em> değiştirilebilir. Yeni kategori eklerken slug'ı doğru belirleyin.
            </p>
          </Section>

          {/* Social */}
          <Section
            testId="section-social"
            title="Sosyal Medya Bağlantıları"
            description="Footer'da yalnızca URL girilen sosyal medya ikonları görünür."
          >
            <div className="grid gap-3">
              {[
                { key: 'facebook', label: 'Facebook', Icon: Facebook, placeholder: 'https://facebook.com/haberturkiyeden' },
                { key: 'twitter', label: 'Twitter / X', Icon: Twitter, placeholder: 'https://x.com/haberturkiyeden' },
                { key: 'instagram', label: 'Instagram', Icon: Instagram, placeholder: 'https://instagram.com/haberturkiyeden' },
                { key: 'youtube', label: 'YouTube', Icon: Youtube, placeholder: 'https://youtube.com/@haberturkiyeden' },
                { key: 'linkedin', label: 'LinkedIn', Icon: Linkedin, placeholder: 'https://linkedin.com/company/haberturkiyeden' },
              ].map(({ key, label, Icon, placeholder }) => (
                <div key={key} className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <label className="block text-xs font-semibold text-gray-600 mb-0.5">{label}</label>
                    <Input
                      type="url"
                      value={social[key]}
                      onChange={(e) => setSocial({ ...social, [key]: e.target.value })}
                      placeholder={placeholder}
                      data-testid={`social-${key}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={handleSaveAll} disabled={saving} className="bg-[#0284C7] hover:bg-[#0369A1]">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Kaydediliyor…' : 'Tümünü Kaydet'}
          </Button>
        </div>
      </main>
    </div>
  );
};

export default AdminSettings;
